import { useSyncExternalStore } from 'react'
import { hasAcceptedRecordingPolicy } from '@shared/calls/recording-consent'

/**
 * The signed-in user's durable recording-policy decision (one decision covers
 * making AND receiving calls). Hydrated from the `me` profile on sign-in /
 * refresh and updated by recording-policy-service after accept/decline, so the
 * voice-call controller can read it synchronously before dialing.
 */
export type RecordingPolicyConsentState = {
  /** False until the profile has been read at least once this session. */
  loaded: boolean
  /** null = no decision recorded (notice not yet viewed / confirmed). */
  status: 'ACCEPTED' | 'DECLINED' | null
  acceptedAt: string | null
  noticeVersion: string | null
}

const INITIAL: RecordingPolicyConsentState = {
  loaded: false,
  status: null,
  acceptedAt: null,
  noticeVersion: null,
}

let state: RecordingPolicyConsentState = INITIAL
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

export function getRecordingPolicyConsent(): RecordingPolicyConsentState {
  return state
}

export function setRecordingPolicyConsent(
  next: Omit<RecordingPolicyConsentState, 'loaded'>,
): void {
  state = { ...next, loaded: true }
  emit()
}

/** Sign-out: forget the previous user's decision. */
export function resetRecordingPolicyConsent(): void {
  if (state === INITIAL) return
  state = INITIAL
  emit()
}

/**
 * Hydrate from any profile-shaped object carrying the Users-row consent
 * attributes (the `me` query / `updateMe` result). The account record on the
 * server is the source of truth; the per-user local copy written here is only
 * a fallback for launches where the server can't be reached in time.
 */
export function hydrateRecordingPolicyConsentFromProfile(profile: {
  userId?: string | null
  recordingPolicyConsentStatus?: string | null
  recordingPolicyConsentAt?: string | null
  recordingPolicyConsentNoticeVersion?: string | null
}): void {
  const raw = (profile.recordingPolicyConsentStatus ?? '').toUpperCase()
  const next = {
    status: raw === 'ACCEPTED' || raw === 'DECLINED' ? raw : null,
    acceptedAt: profile.recordingPolicyConsentAt ?? null,
    noticeVersion: profile.recordingPolicyConsentNoticeVersion ?? null,
  } as const
  setRecordingPolicyConsent(next)
  if (profile.userId) writeCachedRecordingPolicyConsent(profile.userId, next)
}

/** The account accepted the CURRENT recording-policy version. */
export function hasAcceptedCurrentRecordingPolicy(
  consent: Pick<RecordingPolicyConsentState, 'status' | 'noticeVersion'> | null | undefined,
): boolean {
  if (!consent) return false
  return hasAcceptedRecordingPolicy({
    recordingPolicyConsentStatus: consent.status,
    recordingPolicyConsentNoticeVersion: consent.noticeVersion,
  })
}

const CACHE_KEY_PREFIX = 'ascend-recording-policy:'

type CachedRecordingPolicyConsent = Omit<RecordingPolicyConsentState, 'loaded'>

function writeCachedRecordingPolicyConsent(userId: string, value: CachedRecordingPolicyConsent): void {
  try {
    localStorage.setItem(CACHE_KEY_PREFIX + userId, JSON.stringify(value))
  } catch {
    /* storage unavailable — the server read is authoritative anyway */
  }
}

/** Last server-confirmed decision for this account on this device (cache only). */
export function readCachedRecordingPolicyConsent(
  userId: string | null | undefined,
): CachedRecordingPolicyConsent | null {
  if (!userId) return null
  try {
    const raw = localStorage.getItem(CACHE_KEY_PREFIX + userId)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CachedRecordingPolicyConsent>
    const status = parsed.status === 'ACCEPTED' || parsed.status === 'DECLINED' ? parsed.status : null
    return {
      status,
      acceptedAt: typeof parsed.acceptedAt === 'string' ? parsed.acceptedAt : null,
      noticeVersion: typeof parsed.noticeVersion === 'string' ? parsed.noticeVersion : null,
    }
  } catch {
    return null
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useRecordingPolicyConsent(): RecordingPolicyConsentState {
  return useSyncExternalStore(subscribe, getRecordingPolicyConsent, getRecordingPolicyConsent)
}
